import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, AlertTriangle, CheckCircle, Loader2, BarChart, Zap, Search, Quote } from 'lucide-react';
import { TrialService } from '../services/TrialService';
import { useNavigate } from 'react-router-dom';
import { getUniqueQuotes, BusinessQuote } from '../data/businessQuotes';

interface TrialFormData {
  websiteUrl: string;
  email: string;
  companyName: string;
  city: string;
  consent: boolean;
}

/**
 * Composant de formulaire pour l'essai gratuit
 * Permet aux utilisateurs de tester l'outil avec leur propre site
 * Vérifie la correspondance entre le domaine du site et l'email
 */
const TrialRegistrationForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<TrialFormData>({
    websiteUrl: 'https://',
    email: '',
    companyName: '',
    city: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [currentQuote, setCurrentQuote] = useState<BusinessQuote | null>(null);
  const [quotes, setQuotes] = useState<BusinessQuote[]>([]);
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [errors, setErrors] = useState<{
    websiteUrl?: string;
    email?: string;
    companyName?: string;
    city?: string;
    consent?: string;
    domainMatch?: string;
    general?: string;
  }>({});
  const [domainMatch] = useState<boolean | null>(null);

  // Initialiser les citations au montage du composant
  useEffect(() => {
    const uniqueQuotes = getUniqueQuotes(3); // 3 citations pour 30 secondes (1 toutes les 10s)
    setQuotes(uniqueQuotes);
    setCurrentQuote(uniqueQuotes[0]);
  }, []);

  // Gérer la rotation des citations pendant l'analyse
  useEffect(() => {
    if (!isAnalyzing) return;

    // Rotation des citations toutes les 10 secondes
    const quoteInterval = setInterval(() => {
      setQuoteIndex(prev => {
        const nextIndex = (prev + 1) % quotes.length;
        setCurrentQuote(quotes[nextIndex]);
        return nextIndex;
      });
    }, 10000);

    return () => {
      clearInterval(quoteInterval);
    };
  }, [isAnalyzing, quotes]);

  // Fonction pour extraire le domaine d'une URL
  const extractDomain = (url: string): string => {
    try {
      // Retirer protocole (http://, https://)
      let domain = url.replace(/^(https?:\/\/)?(www\.)?/i, '');
      // Retirer chemin, query params, etc.
      domain = domain.split('/')[0];
      // Retirer port si présent
      domain = domain.split(':')[0];
      return domain.toLowerCase();
    } catch (error) {
      return '';
    }
  };

  // Fonction pour extraire le domaine d'un email
  const extractEmailDomain = (email: string): string => {
    try {
      return email.split('@')[1].toLowerCase();
    } catch (error) {
      return '';
    }
  };

  // Vérifier la correspondance des domaines à chaque changement
  useEffect(() => {
    if (formData.websiteUrl && formData.email && formData.email.includes('@')) {
      const siteDomain = extractDomain(formData.websiteUrl);
      const emailDomain = extractEmailDomain(formData.email);

      if (siteDomain && emailDomain) {
        // Vérification exacte ou tolérance sous-domaines
        const isMatch =
          siteDomain === emailDomain ||
          emailDomain.endsWith(`.${siteDomain}`) ||
          siteDomain.endsWith(`.${emailDomain}`);

        if (!isMatch) {
          setErrors(prev => ({
            ...prev,
            domainMatch: `L'email doit correspondre au domaine du site (${siteDomain})`
          }));
        } else {
          setErrors(prev => {
            const { domainMatch: _unused, ...rest } = prev;
            return rest;
          });
        }
      }
    }
  }, [formData.websiteUrl, formData.email]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Validation en temps réel
    if (name === 'websiteUrl') {
      if (!value.startsWith('http')) {
        setErrors(prev => ({
          ...prev,
          websiteUrl: 'L\'URL doit commencer par http:// ou https://'
        }));
      } else {
        setErrors(prev => {
          const { websiteUrl: _unused, ...rest } = prev;
          return rest;
        });
      }
    }

    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setErrors(prev => ({
          ...prev,
          email: 'Format d\'email invalide'
        }));
      } else {
        setErrors(prev => {
          const { email: _unused, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation finale
    const newErrors: Partial<typeof errors> = {};
    
    if (!formData.websiteUrl || formData.websiteUrl === 'https://') {
      newErrors.websiteUrl = 'URL requise';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email requis';
    }
    
    if (!formData.companyName) {
      newErrors.companyName = 'Nom de l\'entreprise requis';
    }
    
    if (!formData.city) {
      newErrors.city = 'Ville requise';
    }
    
    if (!formData.consent) {
      newErrors.consent = 'Vous devez accepter les conditions';
    }
    
    if (domainMatch === false) {
      newErrors.domainMatch = 'L\'email doit correspondre au domaine du site';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      // Appel à l'API Supabase via le TrialService
      const siteDomain = extractDomain(formData.websiteUrl);
      
      // Créer un token unique pour la vérification
      const verificationToken = Math.random().toString(36).substring(2, 15) + 
                               Math.random().toString(36).substring(2, 15);
      
      // Afficher l'écran d'analyse en cours
      setIsAnalyzing(true);
      setIsSubmitting(false);
      setAnalysisStep(1);

      // Créer directement un prospect avec flag trial et lancer l'analyse automatique
      // Toutes les analyses s'exécutent sur le serveur, on attend qu'elles se terminent
      const result = await startTrialProcess(formData.websiteUrl, formData.email, formData.companyName, formData.city, siteDomain, verificationToken);

      if (result.success && result.accessToken) {
        // Analyses terminées, indiquer le succès
        setAnalysisStep(5);

        // Petit délai pour montrer le succès, puis rediriger
        setTimeout(() => {
          navigate(`/analyse/${result.accessToken}`);
        }, 1000);
      } else {
        setIsAnalyzing(false);
        setErrors({
          general: result.message || 'Une erreur est survenue. Veuillez réessayer.'
        });
      }
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      setIsAnalyzing(false);
      setErrors({
        general: 'Une erreur est survenue. Veuillez réessayer.'
      });
    } finally {
      // Ne pas réinitialiser isSubmitting ici car on est en mode analyse
    }
  };

  // Utilise le service TrialService pour démarrer le processus d'essai
  const startTrialProcess = async (
    websiteUrl: string, 
    email: string, 
    companyName: string,
    city: string,
    domain: string, 
    token: string
  ) => {
    // Utilise directement le TrialService simplifié qui gère tout dans la table prospects
    return await TrialService.startTrialProcess(websiteUrl, email, companyName, city, domain, token);
  };

  const isFormValid = 
    formData.websiteUrl && 
    formData.websiteUrl !== 'https://' && 
    formData.email && 
    formData.companyName &&
    formData.city &&
    formData.consent && 
    domainMatch !== false &&
    Object.keys(errors).length === 0;

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 md:p-6">
      {isAnalyzing ? (
        <div className="text-center py-8">
          <div className="flex flex-col items-center justify-center space-y-6">
            <div className="relative w-24 h-24">
              <div className="absolute inset-0 border-4 border-orange-200 rounded-full"></div>
              <div 
                className="absolute inset-0 border-4 border-orange-500 rounded-full border-t-transparent animate-spin"
                style={{ animationDuration: '1.5s' }}
              ></div>
              <div className="absolute inset-0 flex items-center justify-center">
                {analysisStep < 1 && <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />}
                {analysisStep === 1 && <Zap className="w-10 h-10 text-orange-500" />}
                {analysisStep === 2 && <BarChart className="w-10 h-10 text-orange-500" />}
                {analysisStep === 3 && <Search className="w-10 h-10 text-orange-500" />}
                {analysisStep >= 4 && <CheckCircle className="w-10 h-10 text-green-500" />}
              </div>
            </div>
            
            <h3 className="text-xl font-bold text-gray-900">
              {analysisStep < 1 && "Préparation de l'analyse..."}
              {analysisStep === 1 && "Analyse de la vitesse de chargement..."}
              {analysisStep === 2 && "Analyse du référencement SEO..."}
              {analysisStep === 3 && "Analyse de l'expérience utilisateur..."}
              {analysisStep >= 4 && "Analyse terminée !"}
            </h3>
            
            <div className="w-full max-w-md bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-orange-500 h-2.5 rounded-full transition-all duration-500 ease-out" 
                style={{ width: `${(analysisStep / 5) * 100}%` }}
              ></div>
            </div>
            
            <p className="text-gray-600">
              {analysisStep < 4 
                ? "Veuillez patienter pendant que nous analysons votre site web..."
                : "Redirection vers votre rapport d'analyse..."}
            </p>

            {/* Citations inspirantes avec animation */}
            {analysisStep < 4 && currentQuote && (
              <AnimatePresence mode="wait">
                <motion.div
                  key={quoteIndex}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="max-w-2xl mx-auto mt-8 p-6 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <Quote className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <p className="text-gray-700 italic text-base leading-relaxed mb-3">
                        "{currentQuote.text}"
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-orange-200"></div>
                        <p className="text-sm font-semibold text-orange-600">
                          {currentQuote.author}
                        </p>
                        <div className="h-px flex-1 bg-orange-200"></div>
                      </div>
                      {currentQuote.role && (
                        <p className="text-xs text-gray-500 text-center mt-1">
                          {currentQuote.role}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700 mb-1">
                URL de votre site web *
              </label>
              <input
                type="url"
                id="websiteUrl"
                name="websiteUrl"
                value={formData.websiteUrl}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${
                  errors.websiteUrl ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="https://votre-site.ch"
              />
              {errors.websiteUrl && (
                <p className="mt-1 text-xs text-red-600">{errors.websiteUrl}</p>
              )}
            </div>

            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                Nom de votre entreprise *
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${
                  errors.companyName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Nom de votre entreprise"
              />
              {errors.companyName && (
                <p className="mt-1 text-xs text-red-600">{errors.companyName}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email professionnel *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="votre.email@entreprise.ch"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                Ville *
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${
                  errors.city ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Votre ville"
              />
              {errors.city && (
                <p className="mt-1 text-xs text-red-600">{errors.city}</p>
              )}
            </div>
          </div>

          {/* Indicateur de correspondance domaine/email */}
          {domainMatch !== null && (
            <div className={`p-2 rounded-lg ${
              domainMatch ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center">
                {domainMatch ? (
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-red-600 mr-2" />
                )}
                <p className={`text-xs ${domainMatch ? 'text-green-700' : 'text-red-700'}`}>
                  {domainMatch 
                    ? 'Domaine email et site web correspondent' 
                    : errors.domainMatch || 'Domaine email et site web ne correspondent pas'}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="consent"
                name="consent"
                type="checkbox"
                checked={formData.consent}
                onChange={handleInputChange}
                className="focus:ring-orange-500 h-4 w-4 text-orange-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-2 text-xs">
              <label htmlFor="consent" className={`font-medium ${errors.consent ? 'text-red-700' : 'text-gray-700'}`}>
                J'accepte que Smart Impact analyse mon site web
              </label>
              {errors.consent && (
                <p className="mt-1 text-xs text-red-600">{errors.consent}</p>
              )}
            </div>
          </div>

          {errors.general && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-700">{errors.general}</p>
            </div>
          )}

          <div className="text-center">
            <motion.button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className={`inline-flex items-center px-6 py-3 rounded-xl font-bold text-base transition-all duration-300 ${
                isFormValid && !isSubmitting
                  ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              whileHover={isFormValid && !isSubmitting ? { scale: 1.05 } : {}}
              whileTap={isFormValid && !isSubmitting ? { scale: 0.98 } : {}}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="mr-2" size={16} />
                  Lancer l'analyse gratuite
                </>
              )}
            </motion.button>
            
            <p className="text-xs text-gray-500 mt-2">
              * Champs obligatoires
            </p>
          </div>
        </form>
      )}
    </div>
  );
};

export default TrialRegistrationForm;
